import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Icon, Input, LogoMark, Notice, type IconName } from '../design/index.js'
import { Link } from './Link.js'
import { paths } from './router.js'
import { platformApi, PlatformApiProvider, usePlatformApi, type PlatformSessionView } from './platform-api.js'
import { ErrorBoundary } from './ErrorBoundary.js'
import { NoticeBlock } from '../framework/feedback.js'
import { PLATFORM_SECTIONS, PlatformPasswordPage, PlatformSection } from '../pages/PlatformPages.js'

/**
 * The platform console, top to bottom.
 *
 * Kept out of App.tsx's signed-in tree deliberately. That tree assumes a
 * company: its shell has a company switcher, its api client sends `x-company`,
 * and every page it renders takes a companyId. A platform admin has none of
 * that, and threading an "or maybe there is no company" through all of it would
 * put the tenancy boundary inside a conditional. Two trees, one of which never
 * touches the other's client.
 */

const PlatformLogin = ({ onSignedIn }: { onSignedIn: () => void }) => {
  const api = usePlatformApi()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.login(email.trim(), password)
      onSignedIn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth">
      <form onSubmit={submit} className="auth__card">
        <div className="auth__brand">
          <LogoMark size={36} />
          <span className="brand__word">works<span className="brand__bq">bq</span></span>
          <span className="platform-badge">Platform</span>
        </div>
        <h1 className="auth__title">Platform console</h1>
        <p className="auth__lede">
          This console reaches every tenant on this installation. Ordinary accounts sign in{' '}
          <a href="#/">on the main page</a>.
        </p>

        {error ? <Notice kind="bad">{error}</Notice> : null}

        <Field label="Email address" required>
          <Input value={email} onChange={setEmail} type="email" autoComplete="username" autoFocus />
        </Field>
        <Field label="Password" required>
          <Input value={password} onChange={setPassword} type="password" autoComplete="current-password" />
        </Field>

        <div className="auth__submit">
          <Button kind="primary" type="submit" disabled={busy || email === '' || password === ''}>
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </div>
      </form>
    </div>
  )
}

const SECTION_ICONS: Record<string, IconName> = {
  tenants: 'layers',
  people: 'users',
  companies: 'database',
  admins: 'shield',
  audit: 'note',
}

const PlatformShell = ({
  section, session, children,
}: { section: string; session: PlatformSessionView; children: ReactNode }) => {
  const api = usePlatformApi()
  const active = section === '' ? 'tenants' : section

  return (
    <div className="frame">
      <aside className="sidebar" aria-label="Console menu">
        {/*
          The badge is not decoration. Every screen behind this sidebar acts on
          somebody else's data, and the one thing that must never happen is
          mistaking it for the ordinary application.
        */}
        <div className="sidebar__brand">
          <Link to={paths.platform()} className="brand" aria-label="Platform console home">
            <LogoMark size={32} />
            <span className="brand__word">works<span className="brand__bq">bq</span></span>
          </Link>
          <span className="platform-badge">Platform</span>
        </div>

        <nav className="side-nav" aria-label="Sections">
          <span className="side-nav__heading">Console</span>
          {PLATFORM_SECTIONS.map((item) => (
            <Link
              key={item.key}
              to={paths.platform(item.key)}
              className={item.key === active ? 'side-nav__item side-nav__item--active' : 'side-nav__item'}
              aria-current={item.key === active ? 'page' : undefined}
            >
              <Icon name={SECTION_ICONS[item.key] ?? 'doc'} size={18} />
              <span className="side-nav__label">{item.label}</span>
            </Link>
          ))}
        </nav>

        <span className="sidebar__spacer" />

        <nav className="side-nav side-nav--foot" aria-label="Personal">
          <Link to={paths.platform('password')} className={active === 'password' ? 'side-nav__item side-nav__item--active' : 'side-nav__item'}>
            <Icon name="gear" size={18} />
            <span className="side-nav__label">Your password</span>
          </Link>
          <a href="#/" className="side-nav__item">
            <Icon name="left" size={18} />
            <span className="side-nav__label">Leave the console</span>
          </a>
        </nav>

        <div className="sidebar__user">
          <span className="avatar avatar--ink" aria-hidden="true">{session.name.trim().slice(0, 1).toUpperCase() || '?'}</span>
          <span className="sidebar__who">
            <span className="sidebar__name">{session.name}</span>
            <span className="sidebar__email" title={session.email}>{session.email}</span>
          </span>
          <button
            type="button"
            className="icon-btn"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => {
              void api.logout().finally(() => { window.location.reload() })
            }}
          >
            <Icon name="logout" size={18} />
          </button>
        </div>
      </aside>

      <div className="frame__main">
        <main className="frame__sheet" style={{ marginTop: 'var(--s2)' }}>{children}</main>
      </div>
    </div>
  )
}

export const PlatformApp = ({ section }: { section: string }) => {
  const client = useQueryClient()
  // Above the ErrorBoundary, whose key is the section: moving from a tenant's
  // row to its companies must not lose which tenant was chosen.
  const [tenantId, setTenantId] = useState('')
  const session = useQuery({
    queryKey: ['platformSession'],
    queryFn: () => platformApi.session(),
    retry: false,
    staleTime: 5 * 60_000,
  })

  if (session.isPending) {
    return <div style={{ minHeight: '100vh', background: 'var(--canvas)' }} />
  }

  if (session.isError || !session.data) {
    return (
      <PlatformApiProvider value={platformApi}>
        <PlatformLogin onSignedIn={() => { void client.invalidateQueries({ queryKey: ['platformSession'] }) }} />
      </PlatformApiProvider>
    )
  }

  const me = session.data
  const nudge = me.mustChangePassword && section !== 'password'

  return (
    <PlatformApiProvider value={platformApi}>
      <PlatformShell section={section} session={me}>
        {nudge ? (
          <div className="page-banner">
            <NoticeBlock tone="warn">
              You are still using the password this account was issued.{' '}
              <a href={paths.platform('password')}>Choose your own</a>.
            </NoticeBlock>
          </div>
        ) : null}
        <ErrorBoundary key={section}>
          {section === 'password'
            ? <PlatformPasswordPage onChanged={() => { void client.invalidateQueries({ queryKey: ['platformSession'] }) }} />
            : (
              <PlatformSection
                section={section}
                adminId={me.adminId}
                tenantId={tenantId}
                onTenant={setTenantId}
              />
            )}
        </ErrorBoundary>
      </PlatformShell>
    </PlatformApiProvider>
  )
}
